import { Injectable } from '@angular/core';
import { StockState } from '../interfaces';
import { Observable, of } from 'rxjs';
import { BrandService } from './brand.service';
import { CategoryService } from './category.service';
import { SubCategoryService } from './sub-category.service';
import { RangeService } from './range.service';
import { paysList } from '../interfaces/pays';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {

  constructor(
    private brandService: BrandService,
    private rangeService: RangeService,
    private categoryService: CategoryService,
    private subCategoryService: SubCategoryService,
  ) {}

  public getStateSeverity(state: StockState | null | undefined): 'success' | 'info' | 'warn' | 'danger' | 'contrast' {
    switch (state) {
      case 'NEUF':
        return 'success'
      case 'RECONDITIONNE':
        return 'info'
      case 'OCCASION':
        return 'warn'
      case 'HS':
        return 'danger'
      default:
        return 'contrast';
    }
  }

  public getStateLabel(state: StockState | null | undefined): string {
    switch (state) {
      case 'NEUF':
        return 'Neuf'
      case 'RECONDITIONNE':
        return 'Reconditionné'
      case 'OCCASION':
        return 'Occasion'
      case 'HS':
        return 'Défectueux'
      default:
        return '';
    }
  }

  public getSectorClassColor(sectorName: string | null | undefined): string {
    switch (sectorName) {
      case 'BLEU':
        return 'bg-blue-500 text-white';
      case 'GRIS':
        return 'bg-gray-500 text-white';
      case 'JAUNE':
        return 'bg-yellow-500 text-white';
      case 'NOIR':
        return 'bg-black text-white';
      case 'ROUGE':
        return 'bg-red-500 text-white';
      case 'VERT':
        return 'bg-green-500 text-white';
      default:
        return '';
    }
  }

  public getSelectOptionsObservable(property: string, filter: { [x: string]: string | number } = {}): Observable<any> {
    let observable: Observable<any> = new Observable<any>();
    const defaultFilter = {size: 10, order_by: 'nom', order: 'ASC'}

    switch (property) {
      case 'marque':
        observable = this.brandService.getCollection({...defaultFilter, ...filter, fields: {marques: 'id,nom'}})
        break;
      case 'gamme':
        observable = this.rangeService.getCollection({...defaultFilter, ...filter, fields: {gammes: 'id,nom'}})
        break;
      case 'categorie':
        observable = this.categoryService.getCollection({...defaultFilter, ...filter, fields: {categories: 'id,nom'}})
        break;
      case 'sous_categorie':
        observable = this.subCategoryService.getCollection({...defaultFilter, ...filter, fields: {"sous_categories": 'id,nom'}})
        break;
      case 'pays_origine':
        observable = of({
          results: paysList.map((pays) => {
          return {id: pays.nom, nom: pays.nom}
        })});
        break;
    }

    return observable;
  }

  public getEbaySeverity(etat: number | null): 'success' | 'warn' | 'danger' | 'contrast' {
    switch (etat) {
      case 1:
        return 'danger'
      case 2:
        return 'warn'
      case 3:
        return 'success'
      default:
        return 'contrast';
    }
  }

  public getEbayLabel(etat: number | null, short: boolean = true): string {
    switch (etat) {
      case 1:
        return short ? 'Aucun stock' : 'Aucun stock sur Ebay'
      case 2:
        return short ? 'Au moins un stock absent' : 'Au moins un stock absent sur Ebay'
      case 3:
        return short ? 'Tous les stocks' : 'Tous les stocks sur Ebay'
      default:
        return '';
    }
  }

  public getQuantitySeverity(etat: number | null): 'success' | 'warn' | 'danger' | 'contrast' {
    switch (etat) {
      case 1:
        return 'danger'
      case 2:
        return 'warn'
      case 3:
        return 'success'
      default:
        return 'contrast';
    }
  }

  public getQuantityLabel(etat: number | null, short: boolean = true): string {
    switch (etat) {
      case 1:
        return short ? 'Aucun produit' : 'Aucun produit en stock'
      case 2:
        return short ? 'Au moins un produit' : 'Au moins un produit en stock'
      case 3:
        return short ? 'Au moins 4 produits' : 'Au moins 4 produits en stock'
      default:
        return '';
    }
  }

  public getFiabilityClass(fiability: number | null | undefined): string {
    if (fiability === null || fiability === undefined) {
      return '';
    } else if (fiability >= 70) {
      return 'text-green-500 dark:text-green-400';
    } else if (fiability >= 30) {
      return 'text-yellow-500 dark:text-yellow-400';
    } else {
      return 'text-red-500 dark:text-red-400';
    }
  }

  public getPriceAbsoluteColor(priceDiff: number | null | undefined): string {
      if (priceDiff === null || priceDiff === undefined) {
          return '';
      } else if (Math.abs(priceDiff) >= 10 && Math.abs(priceDiff) < 25) {
          return 'text-yellow-500 dark:text-yellow-400';
      } else if (Math.abs(priceDiff) >= 25 && Math.abs(priceDiff) < 40) {
          return 'text-orange-500 dark:text-orange-400';
      } else if (Math.abs(priceDiff) >= 40) {
          return 'text-red-500 dark:text-red-400';
      } else {
          return '';
      }
  }

  public getHistoryActionLabel(action: string | null | undefined): string {
      switch (action) {
          case 'ADDITION':
              return 'ajoutée(s)';
          case 'SOUSTRACTION':
              return 'retirée(s)';
          case 'CREATION':
              return 'initialisée(s)';
          case 'SUPPRESSION':
              return 'supprimée(s)';
          default:
              return '';
      }
  }

  public getHistoryActionClass(action: string | null | undefined): string {
      switch (action) {
          case 'ADDITION':
              return 'text-green-500 dark:text-green-400';
          case 'SOUSTRACTION':
              return 'text-orange-500 dark:text-orange-400';
          case 'CREATION':
              return 'text-blue-500 dark:text-blue-400';
          case 'SUPPRESSION':
              return 'text-red-500 dark:text-red-400 font-bold';
          default:
              return '';
      }
  }

    formatDate(timestamp: number): string {
        const date = moment(timestamp);
        return date.format('MMM YY');
    }

    formatNumber(value: number): string {
        return new Intl.NumberFormat('fr-FR').format(value);
    }
}
